// TODO: Car Tasks Advice: I recommend holding off on the manual "Todo list" tasks for now. 
// To truly be a "Decision Support Tool," we should eventually pivot "Tasks" into Predictive Insights 
// (e.g., "AI predicts your timing belt will need attention in ~10k km"). 
// Managing manual tasks feels like "tracker" work, whereas predictions feel like a premium decision tool.

import { useState, useEffect, useMemo } from 'react';
import { callRpc } from '../../lib/rpcClient';
import { Sparkles, Activity, Users, MessageCircle, User, ChevronDown, ChevronRight, MessageSquare, MousePointer2, TrendingUp, Calendar } from 'lucide-react';
import { format } from 'date-fns';

type ViewMode = 'activity' | 'users' | 'feedback';

export function AdminCarTasksStats() {
    const [stats, setStats] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<ViewMode>('activity');
    const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());

    const filteredStats = useMemo(() => {
        return stats.filter(s => 
            s.event_type === 'car_tasks_opened' || 
            s.event_type === 'car_tasks_pressed_get_started'
        );
    }, [stats]);

    const summary = useMemo(() => {
        const opened = filteredStats.filter(s => s.event_type === 'car_tasks_opened').length;
        const interested = filteredStats.filter(s => s.event_type === 'car_tasks_pressed_get_started').length;
        const totalUnique = new Set(filteredStats.map(s => s.user_id)).size;

        return {
            total_views: opened,
            total_interested: interested,
            unique_users: totalUnique,
            conversion_rate: opened > 0 ? (interested / opened * 100).toFixed(1) : '0'
        };
    }, [filteredStats]);

    const groupedStats = useMemo(() => {
        const groups: Record<string, { 
            user_id: string, 
            email: string, 
            events: any[], 
            counts: Record<string, number>,
            last_activity: string 
        }> = {};

        filteredStats.forEach(s => {
            if (!groups[s.user_id]) {
                groups[s.user_id] = {
                    user_id: s.user_id,
                    email: s.user_email || 'Anonymous',
                    events: [],
                    counts: { car_tasks_opened: 0, car_tasks_pressed_get_started: 0 },
                    last_activity: s.created_at
                };
            }
            groups[s.user_id].events.push(s);
            groups[s.user_id].counts[s.event_type] = (groups[s.user_id].counts[s.event_type] || 0) + 1;
            if (new Date(s.created_at) > new Date(groups[s.user_id].last_activity)) {
                groups[s.user_id].last_activity = s.created_at;
            }
        });

        return Object.values(groups).sort((a, b) => new Date(b.last_activity).getTime() - new Date(a.last_activity).getTime());
    }, [filteredStats]);

    const feedbackStats = useMemo(() => {
        return filteredStats.filter(s => s.feedback && s.feedback.trim().length > 0);
    }, [filteredStats]);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await callRpc('get_admin_pricing_stats');
            setStats(data || []);
        } catch (err: any) {
            console.error('Error fetching car tasks stats:', err);
            setError(err.message || 'Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    const toggleUser = (userId: string) => {
        const next = new Set(expandedUsers);
        if (next.has(userId)) next.delete(userId);
        else next.add(userId);
        setExpandedUsers(next);
    };

    const getEventBadge = (type: string) => {
        switch (type) {
            case 'car_tasks_opened':
                return <span className="bg-amber-500/10 text-amber-500 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest leading-none border border-amber-500/20">Viewed Demo</span>;
            case 'car_tasks_pressed_get_started':
                return <span className="bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest leading-none border border-emerald-500/20">Interested</span>;
            default:
                return <span className="bg-slate-800 text-slate-400 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest leading-none border border-slate-700">{type?.replace('_', ' ')}</span>;
        }
    };

    return (
        <div className="space-y-8 pb-12 font-primary">
            {/* Header */}
            <div className="flex justify-between items-start">
                <div>
                    <h1 className="text-3xl font-black tracking-tight text-white mb-2 flex items-center gap-3">
                        <Sparkles className="text-blue-500" size={32} />
                        Car Tasks Analytics
                    </h1>
                    <p className="text-slate-500 font-bold uppercase text-[10px] tracking-[0.2em]">Maintenance feature validation dashboard.</p>
                </div>
                <button 
                    onClick={fetchStats}
                    disabled={loading}
                    className="p-3 bg-slate-900 border border-slate-800 rounded-2xl text-slate-400 hover:text-white hover:border-slate-700 transition-all shadow-xl active:scale-95 disabled:opacity-50"
                >
                    <Activity className={loading ? 'animate-spin' : ''} size={20} />
                </button>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-3xl shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-blue-500/10 rounded-xl">
                            <Activity size={18} className="text-blue-400" />
                        </div>
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Total Views</span>
                    </div>
                    <div className="text-3xl font-black text-white">{summary.total_views}</div>
                </div>

                <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-3xl shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-emerald-500/10 rounded-xl">
                            <Sparkles size={18} className="text-emerald-400" />
                        </div>
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Interested</span>
                    </div>
                    <div className="text-3xl font-black text-white">{summary.total_interested}</div>
                </div>

                <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-3xl shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-orange-500/10 rounded-xl">
                            <Users size={18} className="text-orange-400" />
                        </div>
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Unique Users</span>
                    </div>
                    <div className="text-3xl font-black text-white">{summary.unique_users}</div>
                </div>

                <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-3xl shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-purple-500/10 rounded-xl">
                            <TrendingUp size={18} className="text-purple-400" />
                        </div>
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Conversion Rate</span>
                    </div>
                    <div className="text-3xl font-black text-white">{summary.conversion_rate}%</div>
                </div>
            </div>

            {/* Tab bar */}
            <div className="flex p-1.5 bg-slate-900 border border-slate-800 rounded-2xl w-fit">
                {[
                    { id: 'activity', label: 'All Activity', icon: Activity },
                    { id: 'users', label: 'Group by User', icon: Users },
                    { id: 'feedback', label: 'Feedback Only', icon: MessageCircle }
                ].map(mode => (
                    <button
                        key={mode.id}
                        onClick={() => setViewMode(mode.id as ViewMode)}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest transition-all ${viewMode === mode.id ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20' : 'text-slate-500 hover:text-slate-300 hover:bg-white/5'}`}
                    >
                        <mode.icon size={14} />
                        {mode.label}
                    </button>
                ))}
            </div>

            {/* List/Table Container */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-3xl overflow-hidden shadow-xl">
                {loading ? (
                    <div className="p-20 flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    </div>
                ) : error ? (
                    <div className="p-16 text-center">
                        <div className="text-red-400 font-bold mb-2">Error loading data</div>
                        <div className="text-slate-500 text-sm italic">{error}</div>
                    </div>
                ) : (
                    <>
                        {/* 1. All Activity View */}
                        {viewMode === 'activity' && (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="border-b border-slate-800 bg-slate-800/30 text-slate-500 text-[10px] font-black uppercase tracking-widest">
                                            <th className="px-6 py-4">When</th>
                                            <th className="px-6 py-4">User</th>
                                            <th className="px-6 py-4">Action</th>
                                            <th className="px-6 py-4">Info/Feedback</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-800">
                                        {filteredStats.map((item, idx) => (
                                            <tr key={idx} className="hover:bg-white/5 transition-colors group">
                                                <td className="px-6 py-4 text-xs text-slate-400 whitespace-nowrap">
                                                    {format(new Date(item.created_at), 'dd MMM, HH:mm')}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex flex-col">
                                                        <span className="text-white font-bold text-sm truncate max-w-[180px]">{item.user_email || 'Anonymous'}</span>
                                                        <span className="text-[9px] text-slate-600 font-mono">{item.user_id.split('-')[0]}...</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    {getEventBadge(item.event_type)}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="max-w-md">
                                                        {item.feedback ? (
                                                            <div className="text-sm text-slate-300 leading-relaxed border-l-2 border-blue-500/30 pl-4 py-1 italic">
                                                                {item.feedback}
                                                            </div>
                                                        ) : (
                                                            <span className="text-slate-800 italic text-xs">No feedback</span>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* 2. Grouped Users View */}
                        {viewMode === 'users' && (
                            <div className="divide-y divide-slate-800">
                                {groupedStats.map((group) => {
                                    const isExpanded = expandedUsers.has(group.user_id);
                                    return (
                                        <div key={group.user_id} className="flex flex-col">
                                            <div 
                                                className="p-6 flex items-center justify-between cursor-pointer hover:bg-white/5 transition-all"
                                                onClick={() => toggleUser(group.user_id)}
                                            >
                                                <div className="flex items-center gap-4">
                                                    <div className="p-3 bg-slate-900 border border-slate-800 rounded-2xl group-hover:bg-slate-800 transition-all">
                                                        <User className="text-slate-500" size={20} />
                                                    </div>
                                                    <div>
                                                        <span className="text-white font-bold block">{group.email}</span>
                                                        <span className="text-[10px] text-slate-500 font-black uppercase tracking-widest">
                                                            Last activity: {format(new Date(group.last_activity), 'dd MMM, HH:mm')}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-6">
                                                    <div className="flex gap-2">
                                                        <div className="bg-slate-800 border border-slate-700 px-3 py-1.5 rounded-xl text-center min-w-[70px]">
                                                            <span className="block text-[8px] text-slate-500 font-black uppercase tracking-widest leading-none mb-1">Views</span>
                                                            <span className="text-xs text-white font-black">{group.counts.car_tasks_opened || 0}</span>
                                                        </div>
                                                        <div className={`bg-slate-800 border px-3 py-1.5 rounded-xl text-center min-w-[70px] ${group.counts.car_tasks_pressed_get_started ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-slate-700'}`}>
                                                            <span className="block text-[8px] text-slate-500 font-black uppercase tracking-widest leading-none mb-1">Interested</span>
                                                            <span className={`text-xs font-black ${group.counts.car_tasks_pressed_get_started ? 'text-emerald-500' : 'text-white'}`}>{group.counts.car_tasks_pressed_get_started || 0}</span>
                                                        </div>
                                                    </div>
                                                    <div className="text-slate-600">
                                                        {isExpanded ? <ChevronDown size={20} className="rotate-180 transition-all" /> : <ChevronRight size={20} />}
                                                    </div>
                                                </div>
                                            </div>

                                            {isExpanded && (
                                                <div className="px-6 pb-6 bg-slate-950/30">
                                                    <div className="border-t border-slate-800/50 pt-4 space-y-2">
                                                        {group.events.map((e, idx) => (
                                                            <div key={idx} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-xl border border-white/5">
                                                                <div className="flex items-center gap-3">
                                                                    <div className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center">
                                                                        <Calendar size={14} className="text-slate-600" />
                                                                    </div>
                                                                    <span className="text-xs text-slate-400">{format(new Date(e.created_at), 'dd MMM, HH:mm:ss')}</span>
                                                                </div>
                                                                <div className="flex items-center gap-3">
                                                                    {e.feedback && <span className="text-[10px] text-blue-400 italic font-medium px-3 py-1 bg-blue-500/5 rounded-lg border border-blue-500/10">"{e.feedback}"</span>}
                                                                    {getEventBadge(e.event_type)}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* 3. Feedback View */}
                        {viewMode === 'feedback' && (
                            <div className="divide-y divide-slate-800">
                                {feedbackStats.map((item, idx) => (
                                    <div key={idx} className="p-6 hover:bg-white/5 transition-colors">
                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-blue-500/10 rounded-xl">
                                                    <MessageSquare size={16} className="text-blue-400" />
                                                </div>
                                                <div>
                                                    <span className="text-white font-bold block">{item.user_email || 'Anonymous'}</span>
                                                    <span className="text-[10px] text-slate-500 font-black uppercase tracking-widest">{format(new Date(item.created_at), 'dd MMM yyyy, HH:mm')}</span>
                                                </div>
                                            </div>
                                            {getEventBadge(item.event_type)}
                                        </div>
                                        <div className="bg-slate-900 border border-slate-800 p-5 rounded-2xl border-l-4 border-l-blue-500">
                                            <p className="text-sm text-slate-200 leading-relaxed font-medium italic">"{item.feedback}"</p>
                                        </div>
                                    </div>
                                ))}
                                {feedbackStats.length === 0 && (
                                    <div className="p-16 text-center text-slate-600 font-bold uppercase text-[10px] tracking-[0.2em]">Zero feedback collected.</div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
